import { tCalCreate } from '../../lib/calendar-create-i18n';
import {
  WEEKDAY_CODES, nthOfDay, weekdayText,
  monthlyByDateText, monthlyByWeekdayText, yearlyByDateText, yearlyByWeekdayText,
  type WeekdayCode,
} from './recurrence-form';
import { parseRuleTokens, utcMinusLocalDays } from './parseRecurrenceRule';
import { localDayNumber, shiftWeekday } from './recurrence-dates';

/**
 * ⭐ 「이후 일정」 날짜 이동의 새 규칙 — 화면 미리보기용 (2026-10-14 사용자 확정 · 반복 규칙 1단계 B).
 *
 * 서버 `apps/chat-server/src/modules/calendar/calendar-recurrence-move.ts` 의 `localRecurrenceForMovedStart`
 * 와 «같은 뜻»을 브라우저 현지 시간대로 계산한다(서버는 기관 시간대 — 한 회사 한 시간대 설치라 보통 같다).
 *   매일 ⇒ 매일 · 매주 여러 요일 ⇒ 같은 일수만큼 · 몇째 주 요일 ⇒ 새 날짜에서 다시 셈 ·
 *   말일 ⇒ 새 날짜가 말일이면 말일, 아니면 그 날짜 · 매년 ⇒ 새 월·일 또는 새 몇째 주 요일
 * 저장된 규칙은 UTC 요일·날짜로 적혀 있으므로(서버 localizedRuleTokens) 회차의 «UTC − 현지 날짜 차»로 되돌려 읽는다.
 * ⚠ 같은 사례를 서버 `calendar-recurrence-move.test.ts` 가 잰다 — 한쪽을 고치면 다른 쪽도.
 */

export interface MovedLocalRecurrence {
  freq: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  interval: number;
  /** WEEKLY — 현지 요일(월→일 순). 비면 새 시작의 요일. */
  byDay?: WeekdayCode[];
  /** MONTHLY/YEARLY 요일 방식 — 1~4, 5 = 마지막 주. */
  bysetpos?: number;
  /** 요일 방식의 현지 요일. */
  weekday?: WeekdayCode;
  /** MONTHLY/YEARLY 날짜 방식 — 31 = 말일. */
  bymonthday?: number;
  /** YEARLY — 현지 월. */
  bymonth?: number;
}

const MON_FIRST: readonly WeekdayCode[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

const daysInMonth = (y: number, m1: number) => new Date(y, m1, 0).getDate();

/** 순수 — 몇째 주(1~5, 5 = 마지막 주)를 새 날짜에서 다시 센다(서버 nthForMovedDate 와 같다). */
export function nthForMovedDate(day: number, dim: number, originalLast: boolean): number {
  // 셈은 반복 설정 창의 「몇째 주」(recurrence-form `nthOfDay`)와 같다 — 원래 「마지막 주」였던 규칙만 넷째도 마지막으로 본다.
  const n = nthOfDay(day);
  if (n >= 5) return 5;
  if (originalLast && day + 7 > dim) return 5;
  return n;
}

/**
 * 순수 — 원래 규칙을 현지 뜻으로 읽어, 새 시작 날짜에 맞춘 현지 반복을 만든다. 못 읽으면 null.
 * @param occAt 옮기는 회차의 «원래» 시각(occurrenceAt) — 원래 규칙의 현지 뜻을 읽는 기준
 */
export function movedLocalRecurrence(rule: string | null | undefined, occAt: Date, newStart: Date): MovedLocalRecurrence | null {
  if (!rule || Number.isNaN(occAt.getTime()) || Number.isNaN(newStart.getTime())) return null;
  const t = parseRuleTokens(rule);
  const freq = (t.get('FREQ') ?? '').toUpperCase();
  if (freq !== 'DAILY' && freq !== 'WEEKLY' && freq !== 'MONTHLY' && freq !== 'YEARLY') return null;
  const interval = Math.max(1, Number(t.get('INTERVAL') ?? 1) || 1);
  const base: MovedLocalRecurrence = { freq, interval };
  const byday = (t.get('BYDAY') ?? '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
  const monthDays = (t.get('BYMONTHDAY') ?? '').split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n !== 0);

  if (freq === 'DAILY') return base;
  if (freq === 'WEEKLY') {
    if (byday.length === 0) return { ...base, byDay: [WEEKDAY_CODES[newStart.getDay()]!] };
    // 저장된 규칙은 UTC 요일 — 회차의 «UTC − 현지 날짜 차»로 현지 요일로 되돌린 뒤, 옮긴 일수만큼 민다.
    const oldDelta = utcMinusLocalDays(occAt.toISOString());
    const shift = localDayNumber(newStart) - localDayNumber(occAt);
    const out: WeekdayCode[] = [];
    for (const c of byday) {
      const local = shiftWeekday(WEEKDAY_CODES, c.replace(/^[+-]?\d+/, ''), -oldDelta);
      const moved = local ? shiftWeekday(WEEKDAY_CODES, local, shift) : null;
      if (moved && !out.includes(moved)) out.push(moved);
    }
    if (out.length === 0) return null;
    out.sort((a, b) => MON_FIRST.indexOf(a) - MON_FIRST.indexOf(b));
    return { ...base, byDay: out };
  }
  const y = newStart.getFullYear();
  const m1 = newStart.getMonth() + 1;
  const d = newStart.getDate();
  const dim = daysInMonth(y, m1);
  const yearly = freq === 'YEARLY' ? { bymonth: m1 } : {};
  if (byday.length > 0) {
    const setpos = Number(t.get('BYSETPOS'));
    const originalLast = setpos === -1 || (monthDays.length > 0 && monthDays.every((n) => n < 0));
    return {
      ...base, ...yearly,
      weekday: WEEKDAY_CODES[newStart.getDay()]!,
      bysetpos: nthForMovedDate(d, dim, originalLast),
    };
  }
  const occDim = daysInMonth(occAt.getFullYear(), occAt.getMonth() + 1);
  const monthEnd = monthDays.length > 0 && monthDays.every((n) => n < 0) && !t.has('BYSETPOS') && occAt.getDate() === occDim;
  return { ...base, ...yearly, bymonthday: monthEnd && d === dim ? 31 : d };
}

/** 사람 말 — 「매주 수·금」·「매월 둘째 화요일」·「매월 말일」 등. */
export function describeMovedRecurrence(r: MovedLocalRecurrence): string {
  if (r.freq === 'DAILY') {
    return r.interval > 1 ? tCalCreate('rc.every_n_days', { n: r.interval }) : tCalCreate('rc.tab_daily');
  }
  if (r.freq === 'WEEKLY') {
    const days = (r.byDay ?? []).map(weekdayText).join('·');
    return r.interval > 1
      ? tCalCreate('rc.every_n_weeks_on', { n: r.interval, days })
      : tCalCreate('rc.weekly_on', { days });
  }
  // 매월·매년 문구는 반복 설정 창의 라벨과 같은 짓기(recurrence-form 의 `…Text` 넷)를 쓴다.
  let core: string;
  if (r.weekday && r.bysetpos) {
    core = r.freq === 'MONTHLY'
      ? monthlyByWeekdayText(r.weekday, r.bysetpos)
      : yearlyByWeekdayText(r.bymonth ?? 1, r.weekday, r.bysetpos);
  } else {
    const day = r.bymonthday ?? 1;
    core = r.freq === 'MONTHLY' ? monthlyByDateText(day) : yearlyByDateText(r.bymonth ?? 1, day);
  }
  if (r.interval > 1) {
    return tCalCreate(r.freq === 'MONTHLY' ? 'rc.every_n_months_suffix' : 'rc.every_n_years_suffix', { rule: core, n: r.interval });
  }
  return core;
}

/** 한국어 조사 「으로/로」 — 마지막 한글 글자의 받침으로 고른다(ㄹ 받침은 「로」). */
export function roParticle(word: string): string {
  for (let i = word.length - 1; i >= 0; i -= 1) {
    const c = word.charCodeAt(i);
    if (c >= 0xac00 && c <= 0xd7a3) {
      const jong = (c - 0xac00) % 28;
      return jong === 0 || jong === 8 ? '로' : '으로';
    }
  }
  return '로';
}

/**
 * 범위 창 「이후 일정」 아래에 붙일 미리보기 — 「이후 일정은 매주 수·금으로 반복됩니다.」
 * 못 읽는 규칙이면 null(아무것도 안 붙인다).
 */
export function futureRulePreview(rule: string | null | undefined, occAt: Date, newStart: Date): string | null {
  const r = movedLocalRecurrence(rule, occAt, newStart);
  if (!r) return null;
  const text = describeMovedRecurrence(r);
  return tCalCreate('rc.preview_future', { rule: text, ro: roParticle(text) });
}
